/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

import {
  getIndustryConfig,
  resolveIndustryType,
} from "@/lib/config/industry-config";
import type { IndustryType } from "@/lib/constants/industries";
import type { IndustryTerminology } from "@/lib/constants/terminology";
import { useOrganization } from "@clerk/nextjs";
import { useMemo } from "react";

export interface UseIndustryConfigOptions {
  industryType?: IndustryType | null;
  terminologyOverride?: IndustryTerminology;
}

export function useIndustryConfig(options: UseIndustryConfigOptions = {}) {
  // Always call hooks (React rules)
  // This hook is only called from IndustryProviderInternal (normal mode)
  // IndustryProviderTestMode uses a different implementation, so useOrganization is safe here
  const { organization } = useOrganization();

  const metadataIndustry = (organization?.publicMetadata?.industry ||
    organization?.publicMetadata?.industryType) as string | undefined;

  const industryType = useMemo(() => {
    if (options.industryType) {
      return options.industryType;
    }
    if (metadataIndustry) {
      return resolveIndustryType(metadataIndustry);
    }
    return resolveIndustryType(undefined);
  }, [metadataIndustry, options.industryType]);

  const baseConfig = useMemo(
    () => getIndustryConfig(industryType),
    [industryType],
  );

  if (options.terminologyOverride) {
    return {
      ...baseConfig,
      terminology: options.terminologyOverride,
    };
  }

  return baseConfig;
}
